import React, { useEffect, useMemo, useCallback, useState, memo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useLeadCapture from "../../UseLeadCapture";
import { useStickyBannerScrollTriggers } from "../../../hooks/useStickyBannerScrollTriggers";
import { useStickyBannerMilestonePhase } from "../../../hooks/useStickyBannerMilestonePhase";
import { buildStickyBannerContext } from "../../../utils/buildStickyBannerContext";
import { resolveStickyBannerDisplay, resolveCarouselDisplay } from "../../../utils/resolveStickyBannerMessage";
import {
  BEHAVIOR_MODES,
  SCROLL_RESOLUTION,
  STICKY_PAGE_TYPES
} from "../../../configs/stickyBanner/stickyBannerConfig.types";
import { inferBehaviorMode, inferScrollResolution } from "../../../configs/stickyBanner/commonConfig";
import {
  inferStickyBannerEventType,
  mapReportPhaseToMilestone,
  resolveStickyBannerMessage
} from "../../../configs/stickyBanner/common/stickyBannerResolver";
import "./StickyBottomTalkToExpertBanner.css";

const MessageBody = memo(function MessageBody({ parts }) {
  if (!parts?.length) return null;
  return (
    <>
      {parts.map((part, i) => {
        const key = `${i}-${part.kind}-${part.content?.slice?.(0, 12) ?? ""}`;
        if (part.kind === "highlight") {
          return (
            <span key={key} className="sticky-tts-banner__highlight">
              {part.content}
            </span>
          );
        }
        return <span key={key}>{part.content}</span>;
      })}
    </>
  );
});

/**
 * Config-driven sticky bottom CTA; mirrors TalkToExpertBanner lead + modal open flow.
 *
 * Behavior modes (config.behaviorMode or inferred):
 * - REPORT + milestone: score band × scroll milestone (central resolver + registry).
 * - REPORT_EXTENSION: priority scroll + messageId (e.g. EMSAT).
 * - CONDITIONS: messageRules + defaultMessageId / legacy parts.
 * - EXAM + CONDITIONS: static registry copy (no score matrix / carousel).
 */
export function StickyBottomTalkToExpertBanner({
  config,
  visible,
  evaluationContext = {},
  /** Selected exam display title — used to pick Maths Olympiad matrix on `/reports/:examId` */
  examTitle,
  reportData,
  progressordata,
  onCtaClick
}) {
  const { handelAlerbarLead } = useLeadCapture();

  const behaviorMode = useMemo(() => inferBehaviorMode(config), [config]);
  const scrollResolution = useMemo(() => inferScrollResolution(config), [config]);

  const useMilestoneScroll =
    visible &&
    behaviorMode === BEHAVIOR_MODES.SCROLL &&
    scrollResolution === SCROLL_RESOLUTION.MILESTONE &&
    (config?.scrollMilestones?.length ?? 0) > 0;

  const usePriorityScroll =
    visible &&
    behaviorMode === BEHAVIOR_MODES.SCROLL &&
    scrollResolution === SCROLL_RESOLUTION.PRIORITY &&
    (config?.scrollTriggers?.length ?? 0) > 0;

  const milestoneCommonDefaults = useMemo(
    () => ({
      scrollBottomEnterRatio: config?.scrollBottomEnterRatio,
      scrollBottomExitRatio: config?.scrollBottomExitRatio,
      middleMilestoneTargetId: config?.middleMilestoneTargetId,
      middleScrollEnterRatio: config?.middleScrollEnterRatio,
      middleScrollExitRatio: config?.middleScrollExitRatio,
      bottomThreshold: config?.bottomThreshold
    }),
    [
      config?.scrollBottomEnterRatio,
      config?.scrollBottomExitRatio,
      config?.middleMilestoneTargetId,
      config?.middleScrollEnterRatio,
      config?.middleScrollExitRatio,
      config?.bottomThreshold
    ]
  );

  const milestonePhase = useStickyBannerMilestonePhase({
    milestones: config?.scrollMilestones,
    enabled: useMilestoneScroll,
    commonDefaults: milestoneCommonDefaults
  });

  const { activeScrollTriggerIds, scrollRatio } = useStickyBannerScrollTriggers({
    enabled: usePriorityScroll,
    scrollTriggers: config?.scrollTriggers,
    scrollRatioDebounceMs: 100
  });

  const [carouselIndex, setCarouselIndex] = useState(0);

  const baseContext = useMemo(
    () => ({
      ...buildStickyBannerContext(reportData, progressordata),
      ...evaluationContext
    }),
    [reportData, progressordata, evaluationContext]
  );

  const scrollState = useMemo(
    () => ({
      activeScrollTriggerIds,
      scrollRatio
    }),
    [activeScrollTriggerIds, scrollRatio]
  );

  const display = useMemo(() => {
    if (!config) return null;

    if (behaviorMode === BEHAVIOR_MODES.CAROUSEL) {
      const carouselPayload = resolveCarouselDisplay(config, carouselIndex);
      if (carouselPayload) return carouselPayload;
      return null;
    }

    if (behaviorMode === BEHAVIOR_MODES.CONDITIONS || (!useMilestoneScroll && !usePriorityScroll)) {
      return resolveStickyBannerDisplay(config, baseContext, {
        activeScrollTriggerIds: new Set(),
        scrollRatio: 0
      });
    }

    if (useMilestoneScroll && config.pageType === STICKY_PAGE_TYPES.REPORT) {
      const milestone = mapReportPhaseToMilestone(milestonePhase);
      const eventType = inferStickyBannerEventType(config, examTitle);
      return resolveStickyBannerMessage({
        pageType: STICKY_PAGE_TYPES.REPORT,
        eventType,
        scorePercent: baseContext.scorePercent,
        milestone,
        defaultCta: config.defaultCtaText,
        subjectSections: baseContext.subjectSections
      });
    }

    if (useMilestoneScroll) {
      return resolveStickyBannerDisplay(config, baseContext, {
        activeScrollTriggerIds: new Set(),
        scrollRatio: 0
      });
    }

    return resolveStickyBannerDisplay(config, baseContext, scrollState);
  }, [
    config,
    behaviorMode,
    carouselIndex,
    useMilestoneScroll,
    usePriorityScroll,
    milestonePhase,
    baseContext,
    scrollState,
    examTitle,
    visible
  ]);

  useEffect(() => {
    if (!visible || behaviorMode !== BEHAVIOR_MODES.CAROUSEL) {
      return undefined;
    }
    const idCount = config?.carouselMessageIds?.length ?? config?.carouselMessages?.length ?? 0;
    if (idCount <= 1) {
      return undefined;
    }
    const ms = config.carouselIntervalMs ?? 5000;
    const id = window.setInterval(() => {
      setCarouselIndex((i) => i + 1);
    }, ms);
    return () => clearInterval(id);
  }, [visible, behaviorMode, config?.carouselIntervalMs, config?.carouselMessageIds?.length, config?.carouselMessages?.length]);

  useEffect(() => {
    if (visible) {
      document.body.classList.add("sticky-tts-banner-active");
    } else {
      document.body.classList.remove("sticky-tts-banner-active");
    }
    return () => {
      document.body.classList.remove("sticky-tts-banner-active");
    };
  }, [visible]);

  const handleCta = useCallback(() => {
    const source = config?.leadCaptureSource ?? "bottom_banner_tte";
    handelAlerbarLead({ source });
    onCtaClick?.();
  }, [config?.leadCaptureSource, handelAlerbarLead, onCtaClick]);

  if (!visible || !config || !display) {
    return null;
  }

  const motionKey = `${display.matchedRuleId}-${display.source}-${display.milestone ?? ""}-${display.scoreBandId ?? ""}`;

  return (
    <>
      <div className="sticky-tts-banner-spacer" aria-hidden />
      <div className="sticky-tts-banner" role="region" aria-label="Talk to an expert">
        <div className="sticky-tts-banner__edge sticky-tts-banner__edge--left" aria-hidden />
        <div className="sticky-tts-banner__edge sticky-tts-banner__edge--right" aria-hidden />
        <div className="sticky-tts-banner__inner">
          <div className="sticky-tts-banner__group">
            <div className="sticky-tts-banner__message">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={motionKey}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  <MessageBody parts={display.messageParts} />
                </motion.div>
              </AnimatePresence>
            </div>
            <button type="button" className="sticky-tts-banner__cta" onClick={handleCta}>
              {display.ctaText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
